<script>
/* COMPOSANTS */
import GaView from '@components/v2/layout/GaView.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkListFilter from '@components/v2/filter/AkListFilter.vue';
import AkAutocompleteFilter from '@components/v2/filter/AkAutocompleteFilter.vue';
import AkAllFilter from '@components/v2/filter/AkAllFilter.vue';
import AkInputFilter from '@components/v2/filter/AkInputFilter.vue';
import AkInputSwitch from '@components/v2/input/AkInputSwitch.vue';

/* MIXINS */
import permissionsMixin from '@mixins/permissionsMixin';
import utilsMixin from '@mixins/utilsMixin';
import sidebarMixin from '@mixins/sidebarMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';
import companyMixin from '@mixins/companyMixin';
import accommodationAutocompleteMixin from '@mixins/autocomplete/accommodationAutocompleteMixin';
import filterListMixin from '@mixins/filterListMixin';

/* SERVICES */
import housekeeperService from '@services/housekeeperService';

export default {
  components: {
    AkInputSwitch,
    AkInputFilter,
    AkAllFilter,
    AkAutocompleteFilter,
    AkListFilter,
    GaListView,
    AkButtonAction,
    AkDatatablePagination,
    GaView,
  },
  mixins: [
    permissionsMixin,
    utilsMixin,
    sidebarMixin,
    loaderMixin,
    messageMixin,
    companyMixin,
    accommodationAutocompleteMixin,
    filterListMixin,
  ],
  head() {
    return {
      title: 'housekeeper.list',
    };
  },
  data() {
    return {
      list: [],
      loading: true,
      query: '',
      filter: {
        page: 1,
        limit: 10,
        sort: ['date_created:DESC', 'id:DESC'],
      },
    };
  },
  mounted() {
    this.search();
  },
  methods: {
    search() {
      this.loading = true;
      housekeeperService
        .housekeepers(this.filter)
        .then((data) => {
          this.list = data;
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeEnabled(data) {
      if (data.userId === undefined) {
        this.addError(this.$t('housekeeper.errorNoId'));
        data.userEnabled = false;
        return;
      }
      if (data.userEnabled) this.enable(data);
      else this.disable(data);
    },
    enable(data) {
      housekeeperService
        .enable(data)
        .then(() => {
          this.success(this.$t('user.enabled_success'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        });
    },
    disable(data) {
      housekeeperService
        .disable(data)
        .then(() => {
          this.success(this.$t('user.disabled_success'));
        })
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        });
    },
    resetFilter() {
      this.filter = {
        page: 1,
        limit: 20,
        sort: ['date_created:DESC', 'id:DESC'],
      };
      this.toggleFilterTitle(
        'GaHousekeeperFilterList',
        this.$t('filter_all'),
        this.filter,
        this.search,
        this.resetFilter,
      );
    },
    openFilterPanel() {
      this.toggleFilterTitle(
        'GaHousekeeperFilterList',
        this.$t('filter_all'),
        this.filter,
        this.search,
        this.resetFilter,
      );
    },
    rowClick(event) {
      let data = event.data;
      this.$router.push({
        name: 'housekeeperPage',
        params: {housekeeperId: data.id},
      });
    },
    exportList() {
      this.showTotalLoaderWithAfter(this.$t('export_in_progress'));
      housekeeperService
        .exportList({...this.filter, sort: undefined})
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.hideLoader();
        });
    },
  },
};
</script>

<template>
  <GaView>
    <template #action>
      <AkButtonAction secondary :label="$t('download')" little @click="exportList" />
      <router-link :to="{name: 'housekeeperCreate'}" class="xs btn-primary">
        {{ $t('add') }}
      </router-link>
    </template>
    <template #content>
      <GaListView searchable @search="this.searchQuery">
        <template #filter>
          <AkListFilter
            v-if="isGas()"
            v-model="filter.companyIds"
            :display-filter="true"
            :items="companies"
            :label="$t('employee.company')"
            :multi-selection="true"
            direction="right"
            @search="search" />
          <AkInputFilter v-model="filter.contactCompanyName" :label="$t('contact.companyName')" @search="search" />
          <AkAutocompleteFilter
            v-model="filter.accommodationId"
            :init-method="autocompleteAccommodationById"
            :minLengthToLaunchSearch="3"
            :label="$t('housekeeper.accommodation')"
            :search-label="autocompleteAccommodationQuery"
            :search-method="autocompleteAccommodation"
            direction="right"
            item-display="display"
            item-value="id"
            @search="search" />
          <AkAllFilter @open="openFilterPanel" />
        </template>
        <template #content>
          <AkDatatablePagination :lazy="false" :loading="loading" :value="list">
            <template #empty>
              {{ $t('housekeeper.empty') }}
            </template>
            <Column
              :header="$t('identifier')"
              :sortable="true"
              field="id"
              header-style="width: 100px;"
              sort-field="id" />
            <Column
              :header="$t('interlocutor.housekeeper')"
              :sortable="true"
              header-style="width: 200px;"
              sort-field="contactLastName">
              <template #body="slotProps">
                <router-link :to="{name: 'housekeeperPage', params: {housekeeperId: slotProps.data.id}}">
                  {{ slotProps.data.contactLastName }}&nbsp;{{ slotProps.data.contactFirstName }}
                </router-link>
              </template>
            </Column>
            <Column
              :header="$t('contact.companyName')"
              :sortable="true"
              field="contactCompanyName"
              header-style="width: 200px;"
              sort-field="contactCompanyName" />
            <Column
              :header="$t('partenaire.company')"
              :sortable="true"
              field="companyName"
              header-style="width: 250px;"
              sort-field="companyName" />
            <Column :header="$t('contact.email')" :sortable="true" field="contactEmail" sort-field="contactEmail" />
            <Column
              :header="$t('contact.phone')"
              :sortable="true"
              field="contactPhone"
              header-style="width: 150px;"
              sort-field="contactPhone" />
            <Column
              :header="$t('partenaire.accommodation_number')"
              :sortable="true"
              field="nbAccommodations"
              header-style="width: 120px;"
              sort-field="nbAccommodations" />
            <Column
              :header="$t('user.enabled')"
              :sortable="true"
              body-style="text-align:center"
              header-style="width: 120px;"
              sort-field="userEnabled">
              <template #body="slotProps">
                <AkInputSwitch
                  validationPopup
                  :nameOnPopup="slotProps.data.contactLastName + ' ' + slotProps.data.contactFirstName"
                  :show-icon-popup="slotProps.data.userEnabled === true"
                  v-model="slotProps.data.userEnabled"
                  :disabled="slotProps.data.userId === undefined"
                  @change="changeEnabled(slotProps.data)" />
              </template>
            </Column>
            <Column header-style="width: 50px;">
              <template #body="slotProps">
                <div class="flex items-center gap-2">
                  <router-link :to="{name: 'housekeeperPage', params: {housekeeperId: slotProps.data.id}}">
                    <i class="ga-icon ga-external text-2xl text-grey-900" />
                  </router-link>
                  <router-link :to="{name: 'housekeeperUpdate', params: {housekeeperId: slotProps.data.id}}">
                    <i class="ga-icon ga-edit text-2xl text-grey-900" />
                  </router-link>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>
  </GaView>
</template>
